import React, { useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { CalendarDays, AlertTriangle, X, ArrowRight, Lock } from 'lucide-react';
import {
  MAX_MOVES_PER_LECTURE,
  MAX_MOVES_PER_DAY,
  moveOptions,
  movesUsedFor,
  movesUsedToday,
  dailyMoveLimitReached,
  lectureMoveLimitReached,
  formatMinutes,
  prettyDate,
} from '@/lib/tracker360-moves';

/**
 * Compact premium date-selection modal for rescheduling a single lecture.
 * All move rules are enforced in @/lib/tracker360-moves — this is only the UI.
 */
export default function LectureMoveModal({ open, lecture, fromDate, schedule, preselectDate, onClose, onConfirm }) {
  const [selected, setSelected] = useState(null);
  const [confirmHeavy, setConfirmHeavy] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected(preselectDate || null);
      setConfirmHeavy(false);
    }
  }, [open, lecture?.id, preselectDate]);

  const minutes = Number(lecture?.effectiveDuration) || 0;
  const options = useMemo(
    () => (open && lecture ? moveOptions(schedule, fromDate, minutes) : []),
    [open, lecture, schedule, fromDate, minutes],
  );

  if (!open || !lecture || typeof document === 'undefined') return null;

  const used = movesUsedFor(lecture);
  const lectureLocked = lectureMoveLimitReached(lecture);
  const dayLocked = dailyMoveLimitReached();
  const chosen = options.find((o) => o.date === selected) || null;

  const handleConfirm = () => {
    if (!chosen) return;
    if (chosen.heavyAfterMove && !confirmHeavy) {
      setConfirmHeavy(true);
      return;
    }
    onConfirm(chosen.date);
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 40, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 28 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full sm:max-w-md max-h-[88dvh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl"
        >
          <div className="flex items-start justify-between gap-3 p-5 pb-3">
            <div className="min-w-0">
              <h3 className="text-base font-black text-slate-900 dark:text-slate-50">Move lecture</h3>
              <p className="mt-0.5 truncate text-xs font-medium text-slate-500 dark:text-slate-400">
                {lecture.subject} • {lecture.chapterName} • Lec {lecture.lectureNumber}
              </p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="px-5 pb-5 space-y-4">
            <div className="flex items-center justify-between rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 px-3.5 py-2.5">
              <span className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
                <CalendarDays className="w-3.5 h-3.5 text-primary" />
                {prettyDate(fromDate)}
              </span>
              <span className="text-[11px] font-bold text-slate-500">
                {used} of {MAX_MOVES_PER_LECTURE} moves used
              </span>
            </div>

            {lectureLocked ? (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
                <p className="flex items-center gap-2 text-xs font-black text-amber-600 dark:text-amber-400">
                  <Lock className="w-3.5 h-3.5" /> Move limit reached
                </p>
                <p className="mt-1 text-[11px] leading-relaxed text-amber-700/80 dark:text-amber-300/80">
                  You've already rescheduled this lecture {MAX_MOVES_PER_LECTURE} times. Complete it or adjust your study plan.
                </p>
              </div>
            ) : dayLocked ? (
              <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4">
                <p className="flex items-center gap-2 text-xs font-black text-rose-600 dark:text-rose-400">
                  <AlertTriangle className="w-3.5 h-3.5" /> Daily Move Limit Reached
                </p>
                <p className="mt-1 text-[11px] text-rose-700/80 dark:text-rose-300/80">
                  You can move up to {MAX_MOVES_PER_DAY} lectures per day.
                </p>
              </div>
            ) : options.length === 0 ? (
              <p className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 text-[11px] text-slate-500">
                No available study day within the next 7 days.
              </p>
            ) : (
              <>
                <div className="space-y-2">
                  {options.map((o) => {
                    const active = selected === o.date;
                    return (
                      <button
                        key={o.date}
                        onClick={() => {
                          setSelected(o.date);
                          setConfirmHeavy(false);
                        }}
                        className={`w-full rounded-xl border px-3.5 py-3 text-left transition-all ${
                          active
                            ? 'border-primary/60 bg-primary/10 shadow-[var(--shadow-glow)]'
                            : 'border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 hover:border-primary/40'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-xs font-bold text-slate-800 dark:text-slate-100">
                            {prettyDate(o.date)}
                            <span className="ml-2 text-[10px] font-semibold text-slate-400">Day {o.dayNumber}</span>
                          </span>
                          <span className="text-[10px] font-bold text-slate-500">
                            {o.lectures} lec • {formatMinutes(o.minutes)}
                          </span>
                        </div>
                        {active && (
                          <p className="mt-1.5 text-[11px] font-medium text-primary">
                            Adds {formatMinutes(minutes)} to this day
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>

                {confirmHeavy && chosen ? (
                  <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
                    <p className="flex items-center gap-2 text-xs font-black text-amber-600 dark:text-amber-400">
                      <AlertTriangle className="w-3.5 h-3.5" /> Heavy Study Day
                    </p>
                    <p className="mt-1 text-[11px] text-amber-700/80 dark:text-amber-300/80">
                      This will add {formatMinutes(minutes)} to your selected day.
                    </p>
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={onClose}
                        className="flex-1 rounded-lg border border-slate-200 dark:border-slate-700 py-2 text-[11px] font-bold text-slate-600 dark:text-slate-300"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => onConfirm(chosen.date)}
                        className="flex-1 rounded-lg bg-amber-500 py-2 text-[11px] font-black text-white"
                      >
                        Move Anyway
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    disabled={!chosen}
                    onClick={handleConfirm}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[image:var(--gradient-brand)] py-3 text-xs font-black text-primary-foreground disabled:opacity-40"
                  >
                    Confirm move <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )}

                <p className="text-center text-[10px] text-slate-400">
                  {movesUsedToday()} of {MAX_MOVES_PER_DAY} daily moves used
                </p>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}
